/**
 * 留资管理 API — 批 9（H9 尾巴）。
 *
 * GET /api/leads?page&size&source → {items, total, page, size}（仅 admin 可见）
 */
import client from "./client";

/** 留资条目（lead 表）。 */
export interface LeadItem {
  id: string;
  company_name: string;
  contact: string;
  message: string | null;
  source_page: string;
  status: string; // new/contacted/closed
  created_at: string | null;
}

export interface LeadListData {
  items: LeadItem[];
  total: number;
  page: number;
  size: number;
}

/** 留资列表（分页 + 来源筛选）。 */
export async function getLeads(params: {
  page?: number;
  size?: number;
  source?: string;
}): Promise<LeadListData> {
  return client.get<unknown, LeadListData>("/leads", { params });
}

/* ── 批 12：后台销售线索管理（/api/admin/leads）── */

export const FOLLOW_STATUSES = ["uncontacted", "contacted", "converted", "invalid"] as const;
export type FollowStatus = (typeof FOLLOW_STATUSES)[number];

export const FOLLOW_STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  uncontacted: { label: "未联系", color: "#b45309", bg: "#fef3c7" },
  contacted: { label: "已联系", color: "#1d4ed8", bg: "#dbeafe" },
  converted: { label: "已转化", color: "#15803d", bg: "#dcfce7" },
  invalid: { label: "无效", color: "#6b7280", bg: "#f3f4f6" },
};

export const DIAG_STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  queued: { label: "排队中", color: "#b45309", bg: "#fef3c7" },
  running: { label: "诊断中", color: "#1d4ed8", bg: "#dbeafe" },
  done: { label: "已完成", color: "#15803d", bg: "#dcfce7" },
  failed: { label: "失败", color: "#b91c1c", bg: "#fee2e2" },
};

/** 最新诊断任务摘要。 */
export interface LeadTaskBrief {
  task_id: string;
  brand_name: string;
  website_url: string;
  status: string;
  completed_at: string | null;
  has_report: boolean;
  report_url: string | null;
}

/** 后台线索条目（含跟进字段 + 最新诊断任务）。 */
export interface AdminLeadItem {
  id: string;
  company_name: string;
  contact: string;
  message: string | null;
  source_page: string;
  status: string;
  follow_status: FollowStatus;
  follow_status_label: string;
  follow_note: string | null;
  followed_at: string | null;
  created_at: string | null;
  latest_task: LeadTaskBrief | null;
}

export interface AdminLeadDetail extends Omit<AdminLeadItem, "latest_task"> {
  latest_task: LeadTaskBrief | null;
  tasks: (LeadTaskBrief & {
    phone: string;
    company_name: string | null;
    scene: string | null;
    error: string | null;
    created_at: string | null;
  })[];
}

/** 线索列表（分页 + 跟进状态筛选 + 手机号/品牌名模糊搜索）。 */
export async function getAdminLeads(params: {
  page?: number;
  size?: number;
  follow_status?: string;
  search?: string;
}): Promise<LeadListData & { items: AdminLeadItem[] }> {
  return client.get<unknown, LeadListData & { items: AdminLeadItem[] }>("/admin/leads", {
    params,
  });
}

/** 线索详情（含全部诊断任务历史）。 */
export async function getAdminLeadDetail(id: string): Promise<AdminLeadDetail> {
  return client.get<unknown, AdminLeadDetail>(`/admin/leads/${id}`);
}

/** 更新跟进状态 / 备注。 */
export async function updateLeadFollow(
  id: string,
  body: { follow_status?: FollowStatus; follow_note?: string },
): Promise<AdminLeadItem> {
  return client.patch<unknown, AdminLeadItem>(`/admin/leads/${id}`, body);
}
